import React, { useEffect, useMemo, useRef, useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import CategorySelect from './CategorySelect';
import Icon from '../../../components/AppIcon';

// IDs exactly match those in fieldSchemas.js and CategorySelect.jsx
const ONLINE_ONLY_SUBCATEGORY_IDS = new Set([
  // Tech & Electronics
  'computers-laptops', 'tablets', 'smartphones', 'tv-home-theater',
  'smart-home-devices', 'audio-equipment', 'headphones', 'wearables',
  // Home Goods
  'major-appliances', 'furniture', 'mattresses', 'home-gym',
  // Travel & Experiences
  'flights', 'hotels', 'vacation-packages', 'cruises', 'rental-cars',
  'travel-insurance', 'concerts', 'sporting-events',
  // Life Events
  'engagement-rings', 'baby-products',
  // Insurance
  'auto-insurance', 'homeowners-insurance', 'renters-insurance',
  'life-insurance', 'health-insurance', 'pet-insurance', 'business-insurance',
  // Education
  'college', 'student-loans', 'bootcamps', 'certifications',
  'online-learning', 'professional-training', 'tutoring',
  // Finance
  'credit-cards',
  // Accessories (all subcategories are online-only)
  'sunglasses', 'watches', 'belts', 'bags', 'wallets',
  'jewelry-rings', 'jewelry-earrings', 'jewelry-bracelets',
  'jewelry-necklaces', 'jewelry-pendants', 'jewelry-anklets',
]);

const STEP_ICONS = [
  { icon: 'ShoppingBag', gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)' },
  { icon: 'FileText',    gradient: 'linear-gradient(135deg, #F06F6B, #E0528A)' },
  { icon: 'MapPin',      gradient: 'linear-gradient(135deg, #E0528A, #F58A54)' },
  { icon: 'Home',        gradient: 'linear-gradient(135deg, #F58A54, #9B59B6)' },
  { icon: 'DollarSign',  gradient: 'linear-gradient(135deg, #F58A54, #E0528A)' },
  { icon: 'Clock',       gradient: 'linear-gradient(135deg, #E0528A, #F06F6B)' },
  { icon: 'MoreHorizontal', gradient: 'linear-gradient(135deg, #F06F6B, #F58A54)' },
  { icon: 'Clock',       gradient: 'linear-gradient(135deg, #E0528A, #F06F6B)' },
  { icon: 'MoreHorizontal', gradient: 'linear-gradient(135deg, #F06F6B, #F58A54)' },
];

const US_STATES = [
  { value: '', label: 'Select state' },
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const ProjectBasicsForm = ({ formData, updateFormData, errors, onProductSubcategorySelected }) => {
  const timelineOptions = [
    { value: '', label: 'Select timeline' },
    { value: '1-week', label: 'Within 1 week' },
    { value: '2-weeks', label: 'Within 2 weeks' },
    { value: '1-month', label: 'Within 1 month' },
    { value: '3-months', label: 'Within 3 months' },
    { value: '6-months', label: 'Within 6 months' },
    { value: 'flexible', label: 'Flexible timeline' },
  ];

  const handleInputChange = (field, value) => {
    updateFormData('basics', { ...formData?.basics, [field]: value });
  };

  // Auto-populate stack name from subcategory + year
  const autoPopulateStackName = (option) => {
    if (!option) return;
    const year = new Date()?.getFullYear();
    const subcategory = option?.label || '';
    const autoName = [subcategory, year]?.filter(Boolean)?.join(' ');
    const isProductSubcategory = ONLINE_ONLY_SUBCATEGORY_IDS?.has(option?.value);
    updateFormData('basics', {
      ...formData?.basics,
      purchaseType: option?.value,
      projectName: autoName,
      ...(isProductSubcategory ? { timeline: '1-week' } : {}),
    });
    setCommittedValues(prev => ({
      ...prev,
      purchaseType: option?.value || '',
      projectName: autoName,
      ...(isProductSubcategory ? { timeline: '1-week' } : {}),
    }));
    if (isProductSubcategory && onProductSubcategorySelected) {
      setTimeout(() => onProductSubcategorySelected(), 100);
    }
  };

  // Track "committed" values (updated on blur) to drive scroll
  const [committedValues, setCommittedValues] = useState({
    purchaseType: formData?.basics?.purchaseType || '',
    projectName:  formData?.basics?.projectName?.trim() || '',
    zipCode:      formData?.basics?.zipCode?.trim() || '',
    address:      formData?.basics?.address?.trim() || '',
    timeline:     formData?.basics?.timeline || '',
  });

  const handleBlur = (field) => {
    const value = formData?.basics?.[field];
    setCommittedValues(prev => ({ ...prev, [field]: typeof value === 'string' ? value?.trim() : value || '' }));
  };

  // Silently auto-set isOnlineOnly based on subcategory
  useEffect(() => {
    const purchaseType = formData?.basics?.purchaseType;
    if (!purchaseType) return;
    const shouldBeOnline = ONLINE_ONLY_SUBCATEGORY_IDS?.has(purchaseType);
    if (shouldBeOnline !== formData?.basics?.isOnlineOnly) {
      updateFormData('basics', { ...formData?.basics, isOnlineOnly: shouldBeOnline });
    }
  }, [formData?.basics?.purchaseType]);

  const purchaseType = formData?.basics?.purchaseType;
  const projectName  = formData?.basics?.projectName?.trim();
  const isOnlineOnly = ONLINE_ONLY_SUBCATEGORY_IDS?.has(purchaseType);
  // Combined address section is "answered" when zipCode is filled
  const zipCode      = formData?.basics?.zipCode?.trim();
  const timeline     = formData?.basics?.timeline;

  const steps = useMemo(() => {
    const base = [
      { key: 'purchaseType', answered: !!purchaseType },
    ];
    if (!isOnlineOnly) {
      base?.push({ key: 'projectName', answered: !!projectName });
      // Single step for the combined service address section — keyed by zipCode
      base?.push({ key: 'zipCode', answered: !!zipCode });
      base?.push(
        { key: 'timeline', answered: !!timeline && timeline !== '' },
        { key: 'notes', answered: true },
      );
    } else {
      base?.push({ key: 'notes', answered: true });
    }
    return base;
  }, [purchaseType, projectName, isOnlineOnly, zipCode, timeline]);

  const fieldRefs = {
    purchaseType: useRef(null),
    projectName:  useRef(null),
    zipCode:      useRef(null),
    timeline:     useRef(null),
    notes:        useRef(null),
  };

  const prevVisibleRef = useRef(new Set(['purchaseType']));

  const isVisible = (key) => {
    const idx = steps?.findIndex(s => s?.key === key);
    if (idx === -1) return false;
    if (idx === 0) return true;
    return steps?.slice(0, idx)?.every(s => s?.answered);
  };

  // Auto-scroll to newly revealed fields
  useEffect(() => {
    const committedPurchaseType = committedValues?.purchaseType;
    const committedProjectName  = committedValues?.projectName;
    const committedIsOnlineOnly = ONLINE_ONLY_SUBCATEGORY_IDS?.has(committedPurchaseType);
    const committedZipCode      = committedValues?.zipCode;
    const committedTimeline     = committedValues?.timeline;

    const committedSteps = [
      { key: 'purchaseType', answered: !!committedPurchaseType },
      { key: 'projectName',  answered: !!committedProjectName },
    ];
    if (!committedIsOnlineOnly) {
      committedSteps?.push({ key: 'zipCode', answered: !!committedZipCode });
    }
    committedSteps?.push(
      { key: 'timeline', answered: !!committedTimeline && committedTimeline !== '' },
      { key: 'notes',    answered: true },
    );

    const committedIsVisible = (key) => {
      const idx = committedSteps?.findIndex(s => s?.key === key);
      if (idx === -1) return false;
      if (idx === 0) return true;
      return committedSteps?.slice(0, idx)?.every(s => s?.answered);
    };

    const allKeys = committedSteps?.map(s => s?.key);
    const currentlyVisible = new Set(allKeys.filter(k => committedIsVisible(k)));
    const prev = prevVisibleRef?.current;

    const newlyVisible = allKeys?.find(k => currentlyVisible?.has(k) && !prev?.has(k));

    if (newlyVisible && fieldRefs?.[newlyVisible]?.current) {
      setTimeout(() => {
        fieldRefs?.[newlyVisible]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }

    prevVisibleRef.current = currentlyVisible;
  }, [committedValues]);

  const fieldAnim = 'transition-all duration-500 ease-in-out';

  return (
    <div className="space-y-6">
      {/* Step 1: Stack Type */}
      <div className={fieldAnim} ref={fieldRefs?.purchaseType}>
        <FieldCard stepIndex={0} title="What do you need?" subtitle="Choose a category to get tailored options">
          <CategorySelect
            label="Stack Type"
            description="What are you looking to purchase?"
            value={formData?.basics?.purchaseType}
            onChange={(value) => {
              handleInputChange('purchaseType', value);
              setCommittedValues(prev => ({ ...prev, purchaseType: value || '' }));
            }}
            onOptionSelect={(option) => autoPopulateStackName(option)}
            error={errors?.purchaseType}
            required
            placeholder="Browse Stacks..."
          />
        </FieldCard>
      </div>

      {/* Step 2: Stack Name — only shown for non-product (service) stacks */}
      {!isOnlineOnly && isVisible('projectName') && (
        <div className={fieldAnim} ref={fieldRefs?.projectName}>
          <FieldCard stepIndex={1} title="Name your stack" subtitle="Give it a memorable name">
            <Input
              label="Stack Name"
              type="text"
              placeholder="e.g., 2024 Family Car Purchase"
              description="Give your stack a memorable name"
              value={formData?.basics?.projectName}
              onChange={(e) => handleInputChange('projectName', e?.target?.value)}
              onBlur={() => handleBlur('projectName')}
              error={errors?.projectName}
              required
            />
          </FieldCard>
        </div>
      )}

      {/* Step 3: Service Address (combined — Address, City, State, Zip) */}
      {!isOnlineOnly && isVisible('zipCode') && (
        <div className={fieldAnim} ref={fieldRefs?.zipCode}>
          <FieldCard stepIndex={2} title="What is the Service Address?" subtitle="Where will the service be performed?">
            <div className="space-y-3">
              <Input
                label="Address"
                type="text"
                placeholder="e.g., 123 Main St"
                value={formData?.basics?.address || ''}
                onChange={(e) => handleInputChange('address', e?.target?.value)}
                onBlur={() => handleBlur('address')}
                error={errors?.address}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="City"
                  type="text"
                  placeholder="e.g., Austin"
                  value={formData?.basics?.city || ''}
                  onChange={(e) => handleInputChange('city', e?.target?.value)}
                  onBlur={() => handleBlur('city')}
                  error={errors?.city}
                />
                <Select
                  label="State"
                  options={US_STATES}
                  value={formData?.basics?.state || ''}
                  searchable
                  onChange={(value) => {
                    handleInputChange('state', value);
                    setCommittedValues(prev => ({ ...prev, state: value || '' }));
                  }}
                  error={errors?.state}
                />
              </div>
              <Input
                label="Zip Code"
                type="text"
                placeholder="e.g., 12345"
                description="Used to find vendors near you"
                value={formData?.basics?.zipCode || ''}
                onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                onBlur={() => handleBlur('zipCode')}
                error={errors?.zipCode}
                required
              />
            </div>
          </FieldCard>
        </div>
      )}

      {/* Step 4: Timeline — only shown for non-product (service) stacks */}
      {!isOnlineOnly && isVisible('timeline') && (
        <div className={fieldAnim} ref={fieldRefs?.timeline}>
          <FieldCard stepIndex={4} title="When do you need this?" subtitle="Set your expected timeline">
            <Select
              label="Timeline"
              description="When do you need this?"
              options={timelineOptions}
              value={formData?.basics?.timeline}
              onChange={(value) => {
                handleInputChange('timeline', value);
                setCommittedValues(prev => ({ ...prev, timeline: value || '' }));
              }}
              error={errors?.timeline}
              required
            />
          </FieldCard>
        </div>
      )}

      {/* Step 5: Additional Notes (always optional, appears last) */}
      {isVisible('notes') && (
        <div className={fieldAnim} ref={fieldRefs?.notes}>
          <FieldCard stepIndex={isOnlineOnly ? 8 : 6} title="Anything else?" subtitle="Optional details for vendors">
            <Input
              label="Additional Notes"
              type="text"
              placeholder="Any specific requirements or preferences..."
              description="Optional details to help vendors understand your needs"
              value={formData?.basics?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              onBlur={() => handleBlur('notes')}
            />
          </FieldCard>
        </div>
      )}
    </div>
  );
};

// Reusable card wrapper for each progressive step
const FieldCard = ({ stepIndex, title, subtitle, children }) => {
  const { icon, gradient } = STEP_ICONS?.[stepIndex] || STEP_ICONS?.[0];
  return (
    <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: gradient }}
        >
          <Icon name={icon} size={17} color="white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-foreground">{title}</h3>
          <p className="text-xs text-text-secondary">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
};

export default ProjectBasicsForm;